import { useEffect } from "react";
import { isLocalDevelopmentHost } from "../utils/domainUtils";
import { safeErrorDiagnostic } from './errorDiagnostics';

const VAPID_PUBLIC_KEY =
  "BEt-NcUcEFFvwxjllrNwBGNOv6ekWMuEfz-TU5cfquzoL-lF8tW_Vb-qWPpW4d2c8alz1YFr2umFtEGgE7BPsEQ";

const PUSH_REGISTRATION_CACHE_KEY = "orgcloud_push_registration_v1";
const PUSH_REGISTRATION_TTL_MS = 24 * 60 * 60 * 1000;

type SubscribeToPushArgs = {
  token: string;
  companyId?: string | null;
};

type SerializableSubscription = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

const canUsePush = () =>
  typeof window !== "undefined" &&
  window.isSecureContext === true &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const isBestEffortPushServerError = (status: number, message: string) =>
  [500, 502, 503, 504].includes(status) &&
  /ProvisionedThroughputExceededException|ThrottlingException|RequestLimitExceeded|Internal server error|Bad Gateway|Service Unavailable|Gateway Timeout/i.test(
    message
  );

function decodeJwtSub(token: string): string {
  try {
    if (!token.includes(".")) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload?.sub || "");
  } catch {
    return "";
  }
}

function buildPushRegistrationFingerprint(args: {
  userId: string;
  companyId?: string | null;
  apiUrl: string;
  subscriptionData: SerializableSubscription;
}) {
  return JSON.stringify({
    userId: args.userId,
    companyId: args.companyId || "",
    apiUrl: args.apiUrl,
    subscription: args.subscriptionData,
  });
}

function resolveApiGatewayBaseUrl(): string {
  const normalizeBase = (base: string): string => (base.endsWith("/") ? base : `${base}/`);
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const useLocalProxy =
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_BUBLOUD_USE_LOCAL_API_PROXY === "true" &&
    !!host &&
    isLocalDevelopmentHost(host);

  if (useLocalProxy) {
    return "/V1/";
  }

  const envBase = (process.env.REACT_APP_API_GATEWAY_BASE_URL || "").trim();
  if (envBase) {
    return normalizeBase(envBase);
  }

  return "https://a9obsrl0ph.execute-api.eu-west-3.amazonaws.com/V1/";
}

function readPushRegistrationCache(): { fingerprint?: string; savedAt?: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PUSH_REGISTRATION_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePushRegistrationCache(fingerprint: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      PUSH_REGISTRATION_CACHE_KEY,
      JSON.stringify({ fingerprint, savedAt: Date.now() })
    );
  } catch {
    // ignore storage failures
  }
}

function isRecentlyRegistered(fingerprint: string) {
  const cachedRegistration = readPushRegistrationCache();
  return (
    cachedRegistration?.fingerprint === fingerprint &&
    typeof cachedRegistration.savedAt === "number" &&
    Date.now() - cachedRegistration.savedAt < PUSH_REGISTRATION_TTL_MS
  );
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function bufferSourceToUint8Array(buffer: BufferSource): Uint8Array {
  if (buffer instanceof ArrayBuffer) {
    return new Uint8Array(buffer);
  }
  return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
}

function subscriptionUsesCurrentVapidKey(subscription: PushSubscription, expectedKey: Uint8Array): boolean {
  const currentKey = subscription.options?.applicationServerKey;
  if (!currentKey) {
    return true;
  }
  return bytesToBase64Url(bufferSourceToUint8Array(currentKey)) === bytesToBase64Url(expectedKey);
}

export async function subscribeToPush({ token, companyId }: SubscribeToPushArgs) {
  if (!token || !companyId || !canUsePush()) {
    console.log("[Push] No se puede usar push: token, cliente activo o capacidades faltantes");
    return;
  }

  if (Notification.permission !== "granted") {
    console.log("[Push] Permiso no concedido");
    return;
  }

  try {
    console.log("[Push] Iniciando suscripcion...");

    const registration = await navigator.serviceWorker.register("/service-worker.js");
    console.log("[Push] Service Worker registrado");

    await registration.update();
    const readyRegistration = await navigator.serviceWorker.ready;
    console.log("[Push] Service Worker listo");

    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    let subscription = await readyRegistration.pushManager.getSubscription();
    if (subscription && !subscriptionUsesCurrentVapidKey(subscription, applicationServerKey)) {
      console.log("[Push] Suscripcion con VAPID antiguo; renovando endpoint");
      await subscription.unsubscribe();
      subscription = null;
    }

    if (subscription) {
      console.log("[Push] Reutilizando suscripcion existente");
    } else {
      console.log("[Push] Creando nueva suscripcion...");
      subscription = await readyRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      console.log("[Push] Suscripcion creada exitosamente");
    }

    const subscriptionData: SerializableSubscription = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
        auth: arrayBufferToBase64(subscription.getKey("auth")),
      },
    };

    const apiGatewayBaseUrl = resolveApiGatewayBaseUrl();
    const apiUrl = `${apiGatewayBaseUrl}accessPushNotifications`;

    const registrationFingerprint = buildPushRegistrationFingerprint({
      userId: decodeJwtSub(token),
      companyId,
      apiUrl,
      subscriptionData,
    });

    if (isRecentlyRegistered(registrationFingerprint)) {
      console.log("[Push] Suscripcion ya registrada recientemente para este usuario/dispositivo");
      return;
    }

    console.log("[Push] Enviando suscripcion al servidor...");

    console.log("[Push] Llamando a accessPushNotifications");

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        subscription: subscriptionData,
        companyId,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorText;
      } catch {
        // keep raw text
      }

      if (response.status === 401) {
        console.log(
          '[Push] Token expirado o invalido. No se puede suscribir ahora.', safeErrorDiagnostic(response)
        );
        return;
      }

      if (response.status === 502 || response.status === 503 || response.status === 504) {
        console.warn(
          '[Push] Servicio de notificaciones push no disponible temporalmente. Se reintentara mas tarde.',
          safeErrorDiagnostic(response)
        );
        return;
      }

      if (isBestEffortPushServerError(response.status, errorMessage)) {
        console.warn(
          '[Push] Registro push diferido por saturacion temporal del backend.',
          safeErrorDiagnostic(response)
        );
        return;
      }

      console.error(
        '[Push] Error al suscribirse a notificaciones push:',
        safeErrorDiagnostic(response)
      );
      throw new Error(`Error ${response.status}: ${errorMessage}`);
    }

    await response.json();
    console.log("[Push] Suscripcion registrada exitosamente en el servidor");
    writePushRegistrationCache(registrationFingerprint);
  } catch (err: any) {
    if (err?.message?.includes("401") || err?.message?.includes("expired")) {
      console.log("[Push] Token expirado, no se puede suscribir. Se reintentara despues del login.");
      return;
    }

    if (
      err?.message?.includes("502") ||
      err?.message?.includes("503") ||
      err?.message?.includes("504") ||
      err?.message?.includes("Bad Gateway") ||
      err?.message?.includes("Service Unavailable") ||
      err?.message?.includes("Gateway Timeout")
    ) {
      console.warn("[Push] Servicio de notificaciones push no disponible temporalmente. Se reintentara mas tarde.");
      return;
    }

    if (isBestEffortPushServerError(500, String(err?.message || ""))) {
      console.warn("[Push] Registro push aplazado por throttling temporal del backend.");
      return;
    }

    if (err?.name === "TypeError" && err?.message?.includes("fetch")) {
      console.warn("[Push] Error de red al intentar suscribirse. Se reintentara mas tarde.");
      return;
    }

    console.error("[Push] Error suscribiendo a notificaciones:", safeErrorDiagnostic(err));
    throw err;
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default function usePushSubscription(token: string, companyId?: string) {
  useEffect(() => {
    if (!token || !canUsePush()) return;
    if (Notification.permission !== "granted") return;

    try {
      if (token.includes(".")) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const exp = payload.exp * 1000;
        const now = Date.now();

        if (exp < now + 5 * 60 * 1000) {
          console.log("[Push] Token expira pronto o esta expirado, no suscribiendo");
          return;
        }
      }
    } catch {
      console.log("[Push] No se pudo verificar el token, no suscribiendo");
      return;
    }

    subscribeToPush({ token, companyId }).catch((error) => {
      if (error?.message?.includes("401") || error?.message?.includes("expired")) {
        console.log("[Push] Token expirado, no se puede suscribir. Se reintentara despues del login.");
      } else {
        console.error("[Push] Error al suscribirse (no critico):", safeErrorDiagnostic(error));
      }
    });
  }, [token, companyId]);
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}
